import { RECIPROCAL_PASSES } from "@/lib/wizard-types";

export interface ExtrasSignals {
  boostCategories: string[];
  penalizeCategories: string[];
  boostKeywords: string[];
  penalizeKeywords: string[];
  boostIndoor: boolean;
  boostOutdoor: boolean;
  hasMembership: string[];
  recentActivities: string[];
  reciprocalMatches: Map<string, string>;
}

/**
 * Parse the wizard "anything else" free-text into ranking signals.
 * Regex-only — no AI. Shared between the explorer (for ranking) and
 * the wizard logger (for anonymized analytics).
 */
export function parseExtras(text: string): ExtrasSignals {
  const lower = (text || "").toLowerCase();
  const signals: ExtrasSignals = {
    boostCategories: [],
    penalizeCategories: [],
    boostKeywords: [],
    penalizeKeywords: [],
    boostIndoor: false,
    boostOutdoor: false,
    hasMembership: [],
    recentActivities: [],
    reciprocalMatches: new Map(),
  };

  if (/outdoor/i.test(lower)) signals.boostOutdoor = true;
  if (/indoor/i.test(lower)) signals.boostIndoor = true;

  const negPatterns = lower.matchAll(/(?:no|hate|skip|not interested in|avoid|don't like|dislike)\s+([a-z][a-z\s-]{2,30}?)(?:[,.\n!]|$)/gi);
  const KNOWN_NEG = new Set([
    "beach","beaches","museum","museums","art","arts","garden","gardens","zoo","aquarium",
    "fishing","hiking","biking","cycling","theater","theatre","movie","movies","bowling",
    "arcade","trampoline","kayak","kayaking","paddleboard","sailing","snorkeling","surfing",
    "picnic","brewery","winery","farm","ranch","circus","comedy","concert","festival",
    "nature","park","parks","water","spa","wellness","sports","crowds","kids","seafood",
    "outdoor","outdoors","indoor","indoors","walking","driving","heat","sun",
  ]);
  for (const m of negPatterns) {
    const term = m[1].trim();
    const head = term.split(/\s+/)[0];
    if (KNOWN_NEG.has(term) || KNOWN_NEG.has(head)) {
      signals.penalizeKeywords.push(KNOWN_NEG.has(term) ? term : head);
      signals.penalizeCategories.push(KNOWN_NEG.has(term) ? term : head);
    }
  }

  const posPatterns = lower.matchAll(/(?:interested in|love|want|like|enjoy|into|looking for|try|hoping for)\s+([a-z\s-]+?)(?:[,.\n!]|$)/gi);
  for (const m of posPatterns) {
    const term = m[1].trim();
    if (term.length > 1) {
      signals.boostKeywords.push(term);
      signals.boostCategories.push(term);
    }
  }

  const knownActivities = [
    "mini golf", "mini-golf", "minigolf", "kayak", "paddleboard", "paddle board",
    "beach", "museum", "art", "garden", "zoo", "aquarium", "fishing", "hiking",
    "biking", "cycling", "theater", "theatre", "movie", "bowling", "arcade",
    "splash pad", "playground", "trampoline", "rock climbing", "escape room",
    "boat", "sailing", "snorkeling", "surfing", "picnic", "brewery", "winery",
    "farm", "ranch", "circus", "magic", "comedy", "concert", "festival",
    "helicopter", "nature", "park", "water park", "waterpark", "spa",
  ];
  for (const activity of knownActivities) {
    if (lower.includes(activity)) {
      if (!signals.penalizeKeywords.some((p) => p.includes(activity) || activity.includes(p))) {
        signals.boostKeywords.push(activity);
      }
    }
  }

  for (const [passKey, passInfo] of Object.entries(RECIPROCAL_PASSES)) {
    for (const keyword of passInfo.keywords) {
      if (lower.includes(keyword)) {
        signals.hasMembership.push(passKey);
        for (const venue of passInfo.reciprocalVenues) {
          signals.reciprocalMatches.set(venue.toLowerCase(), passInfo.benefit);
        }
        break;
      }
    }
  }

  const membershipMatch = lower.match(/(?:have|got|own)\s+(?:a\s+)?(.+?)(?:\s+membership|\s+pass)/);
  if (membershipMatch) signals.hasMembership.push(membershipMatch[1].trim());

  const didMatch = lower.match(/(?:did|went to|just went|visited)\s+(.+?)(?:[,.]|$)/g);
  if (didMatch) {
    didMatch.forEach((m) => {
      const activity = m.replace(/^(?:did|went to|just went|visited)\s+/i, "").trim();
      if (activity.length > 2) {
        signals.recentActivities.push(activity);
        signals.penalizeKeywords.push(activity);
      }
    });
  }

  return signals;
}

/**
 * JSON-safe, PII-scrubbed summary of extras text for analytics storage.
 * Strips email- and phone-like tokens defensively. Drops the live `Map`.
 */
export interface ExtrasSummary {
  boostKeywords: string[];
  penalizeKeywords: string[];
  hasMembership: string[];
  recentActivities: string[];
  boostIndoor: boolean;
  boostOutdoor: boolean;
}

function scrubPII(s: string): string {
  return s
    .replace(/\b[\w.+-]+@[\w-]+\.[\w.-]+\b/gi, "[email]")
    .replace(/\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g, "[phone]");
}

export function summarizeExtras(text: string): ExtrasSummary {
  const s = parseExtras(text);
  const dedupe = (arr: string[]) =>
    Array.from(new Set(arr.map((v) => scrubPII(v).trim()).filter(Boolean))).slice(0, 20);
  return {
    boostKeywords: dedupe(s.boostKeywords),
    penalizeKeywords: dedupe(s.penalizeKeywords),
    hasMembership: dedupe(s.hasMembership),
    recentActivities: dedupe(s.recentActivities),
    boostIndoor: s.boostIndoor,
    boostOutdoor: s.boostOutdoor,
  };
}